import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Search, FileText, Brain, Database, BarChart } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface RAGResponse {
  success: boolean;
  answer: string;
  confidence: number;
  sources: {
    documents: Array<{
      documentId: number;
      filename: string;
      category: string;
      relevanceScore: number;
      summary: string;
      excerpt: string;
    }>;
    memories: Array<{
      type: string;
      content: string;
      relevance: number;
      timestamp: string;
    }>;
    knowledgeGraph: Array<{
      nodeType: string;
      entityName: string;
      relationship: string;
      relevance: number;
    }>;
  };
  recommendations: Array<{
    type: string;
    content: string;
    priority: string;
    reasoning: string;
  }>;
  queryAnalysis: {
    intent: string;
    entities: string[];
    urgency: string;
    nextSteps: string[];
  };
  processingTime: number;
}

interface DocumentSearchResult {
  success: boolean;
  results: Array<{
    documentId: number;
    filename: string;
    category: string;
    relevanceScore: number;
    summary: string;
    excerpt: string;
  }>;
  total: number;
}

export default function RAGTestPage() {
  const [query, setQuery] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [installationId, setInstallationId] = useState('');
  const [currentStage, setCurrentStage] = useState('');
  const { toast } = useToast();

  // Get system statistics
  const { data: ragStats } = useQuery({
    queryKey: ['/api/rag/service/stats'],
    refetchInterval: 30000,
  });

  const { data: docStats } = useQuery({
    queryKey: ['/api/rag/documents/stats'],
    refetchInterval: 30000,
  });

  // Comprehensive RAG query mutation
  const ragQueryMutation = useMutation({
    mutationFn: async (queryData: any) => {
      const response = await fetch('/api/rag/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(queryData),
      });

      if (!response.ok) {
        throw new Error('Failed to process RAG query');
      }

      return response.json() as Promise<RAGResponse>;
    },
    onSuccess: () => {
      toast({
        title: 'Query processed successfully',
        description: 'RAG system has generated a comprehensive response.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Query failed',
        description: error.message,
      });
    },
  });

  // Document search mutation
  const docSearchMutation = useMutation({
    mutationFn: async (searchData: any) => {
      const response = await fetch('/api/rag/documents/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(searchData),
      });

      if (!response.ok) {
        throw new Error('Failed to search documents');
      }

      return response.json() as Promise<DocumentSearchResult>;
    },
    onSuccess: () => {
      toast({
        title: 'Document search completed',
        description: 'Found relevant documents in the index.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Search failed',
        description: error.message,
      });
    },
  });

  // Index rebuild mutation
  const rebuildMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/rag/documents/rebuild-index', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to rebuild index');
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Index rebuilt successfully',
        description: 'Document index has been rebuilt with latest content.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Rebuild failed',
        description: error.message,
      });
    },
  });

  const handleRAGQuery = () => {
    if (!query.trim()) {
      toast({
        variant: 'destructive',
        title: 'Query required',
        description: 'Please enter a query to process.',
      });
      return;
    }

    const queryData = {
      query: query.trim(),
      context: {
        customerId: customerId ? parseInt(customerId) : undefined,
        installationId: installationId ? parseInt(installationId) : undefined,
        currentStage: currentStage || undefined,
      },
      options: {
        includeDocuments: true,
        includeMemory: true,
        includeKnowledgeGraph: true,
        maxResults: 5,
      },
    };

    ragQueryMutation.mutate(queryData);
  };

  const handleDocumentSearch = () => {
    if (!query.trim()) {
      toast({
        variant: 'destructive',
        title: 'Query required',
        description: 'Please enter a search query.',
      });
      return;
    }

    const searchData = {
      query: query.trim(),
      customerId: customerId ? parseInt(customerId) : undefined,
      installationId: installationId ? parseInt(installationId) : undefined,
      maxResults: 10,
    };

    docSearchMutation.mutate(searchData);
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'bg-green-500';
    if (confidence >= 0.6) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'outline';
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-6 w-6" />
            Phase 2 RAG System Testing
          </CardTitle>
          <CardDescription>
            Test the comprehensive RAG implementation with document indexing, memory integration,
            and knowledge graph retrieval
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label htmlFor="query">Query</Label>
              <Textarea
                id="query"
                placeholder="Enter your question about solar installations, customers, or technical support..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
            <div>
              <Label htmlFor="customerId">Customer ID (Optional)</Label>
              <Input
                id="customerId"
                type="number"
                placeholder="e.g., 123"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="installationId">Installation ID (Optional)</Label>
              <Input
                id="installationId"
                type="number"
                placeholder="e.g., 456"
                value={installationId}
                onChange={(e) => setInstallationId(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="currentStage">Current Stage (Optional)</Label>
              <Select value={currentStage} onValueChange={setCurrentStage}>
                <SelectTrigger>
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="New Lead">New Lead</SelectItem>
                  <SelectItem value="Contact Scheduled">Contact Scheduled</SelectItem>
                  <SelectItem value="Site Assessment">Site Assessment</SelectItem>
                  <SelectItem value="Proposal Sent">Proposal Sent</SelectItem>
                  <SelectItem value="Closed Won">Closed Won</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleRAGQuery}
              disabled={ragQueryMutation.isPending}
              className="flex-1"
            >
              {ragQueryMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Brain className="h-4 w-4 mr-2" />
              )}
              Comprehensive RAG Query
            </Button>
            <Button
              onClick={handleDocumentSearch}
              disabled={docSearchMutation.isPending}
              variant="outline"
            >
              {docSearchMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              Document Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* System Statistics */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Document Index Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            {docStats?.success ? (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Total Documents:</span>
                  <Badge variant="secondary">{docStats.stats.totalDocuments}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Index Status:</span>
                  <Badge variant={docStats.stats.isInitialized ? 'default' : 'destructive'}>
                    {docStats.stats.isInitialized ? 'Ready' : 'Initializing'}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <span className="text-sm font-medium">By Category:</span>
                  {Object.entries(docStats.stats.documentsByCategory || {}).map(
                    ([category, count]) => (
                      <div key={category} className="flex justify-between text-sm">
                        <span className="capitalize">{category}:</span>
                        <span>{count}</span>
                      </div>
                    )
                  )}
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">Loading statistics...</p>
            )}
            <Button
              onClick={() => rebuildMutation.mutate()}
              disabled={rebuildMutation.isPending}
              variant="outline"
              size="sm"
              className="w-full mt-4"
            >
              {rebuildMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              Rebuild Index
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart className="h-5 w-5" />
              RAG Service Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ragStats?.success ? (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Service Status:</span>
                  <Badge variant={ragStats.stats.isInitialized ? 'default' : 'destructive'}>
                    {ragStats.stats.isInitialized ? 'Ready' : 'Initializing'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span>Total Queries:</span>
                  <span>{ragStats.stats.totalQueries}</span>
                </div>
                <div className="flex justify-between">
                  <span>Avg Processing Time:</span>
                  <span>{ragStats.stats.averageProcessingTime}ms</span>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">Loading statistics...</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* RAG Query Results */}
      {ragQueryMutation.data && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Comprehensive RAG Response
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {ragQueryMutation.data.processingTime}ms
                </span>
                <div className="flex items-center gap-1">
                  <div
                    className={`w-2 h-2 rounded-full ${getConfidenceColor(ragQueryMutation.data.confidence)}`}
                  />
                  <span className="text-sm">
                    {(ragQueryMutation.data.confidence * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="answer" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="answer">Answer</TabsTrigger>
                <TabsTrigger value="sources">Sources</TabsTrigger>
                <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
                <TabsTrigger value="analysis">Analysis</TabsTrigger>
              </TabsList>

              <TabsContent value="answer" className="space-y-4">
                <div className="prose max-w-none">
                  <p className="whitespace-pre-wrap">{ragQueryMutation.data.answer}</p>
                </div>
              </TabsContent>

              <TabsContent value="sources" className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">
                        Documents ({ragQueryMutation.data.sources.documents.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {ragQueryMutation.data.sources.documents.map((doc, idx) => (
                        <div key={idx} className="p-2 border rounded text-sm">
                          <div className="font-medium">{doc.filename}</div>
                          <div className="text-muted-foreground">{doc.category}</div>
                          <div className="text-xs mt-1">{doc.excerpt}</div>
                          <Badge variant="outline" className="mt-1">
                            {(doc.relevanceScore * 100).toFixed(0)}%
                          </Badge>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">
                        Memories ({ragQueryMutation.data.sources.memories.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {ragQueryMutation.data.sources.memories.map((memory, idx) => (
                        <div key={idx} className="p-2 border rounded text-sm">
                          <div className="font-medium capitalize">{memory.type}</div>
                          <div className="text-xs mt-1">{memory.content.substring(0, 100)}...</div>
                          <Badge variant="outline" className="mt-1">
                            {(memory.relevance * 100).toFixed(0)}%
                          </Badge>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">
                        Knowledge Graph ({ragQueryMutation.data.sources.knowledgeGraph.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {ragQueryMutation.data.sources.knowledgeGraph.map((kg, idx) => (
                        <div key={idx} className="p-2 border rounded text-sm">
                          <div className="font-medium">{kg.entityName}</div>
                          <div className="text-muted-foreground">{kg.relationship}</div>
                          <Badge variant="outline" className="mt-1">
                            {(kg.relevance * 100).toFixed(0)}%
                          </Badge>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="recommendations" className="space-y-2">
                {ragQueryMutation.data.recommendations.map((rec, idx) => (
                  <Card key={idx}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={getPriorityColor(rec.priority)}>{rec.priority}</Badge>
                            <Badge variant="outline">{rec.type}</Badge>
                          </div>
                          <p className="font-medium">{rec.content}</p>
                          <p className="text-sm text-muted-foreground mt-1">{rec.reasoning}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>

              <TabsContent value="analysis" className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Query Analysis</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <span className="font-medium">Intent:</span>
                        <Badge variant="secondary" className="ml-2">
                          {ragQueryMutation.data.queryAnalysis.intent}
                        </Badge>
                      </div>
                      <div>
                        <span className="font-medium">Urgency:</span>
                        <Badge variant="outline" className="ml-2">
                          {ragQueryMutation.data.queryAnalysis.urgency}
                        </Badge>
                      </div>
                      <div>
                        <span className="font-medium">Entities:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {ragQueryMutation.data.queryAnalysis.entities.map((entity, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {entity}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Next Steps</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1 text-sm">
                        {ragQueryMutation.data.queryAnalysis.nextSteps.map((step, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-muted-foreground">{idx + 1}.</span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Document Search Results */}
      {docSearchMutation.data && (
        <Card>
          <CardHeader>
            <CardTitle>Document Search Results</CardTitle>
            <CardDescription>
              Found {docSearchMutation.data.total} documents matching your query
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <div className="space-y-4">
                {docSearchMutation.data.results.map((doc, idx) => (
                  <Card key={idx}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium">{doc.filename}</h4>
                            <Badge variant="outline">{doc.category}</Badge>
                            <Badge variant="secondary">
                              {(doc.relevanceScore * 100).toFixed(0)}% match
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{doc.summary}</p>
                          <p className="text-xs border-l-2 pl-2">{doc.excerpt}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
